import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { usePhishPass } from '../../context/PhishPassContext';
import { Send, Loader2, Plus, Trash2, Search, Mail, Eye, MousePointerClick, Users, Shuffle, AlertCircle } from 'lucide-react';

const CampaignList = () => {
  const {
    campaigns, fetchCampaigns, getCampaign, sendCampaign, deleteCampaign, resetCampaignClicks,
    templates, fetchTemplates, previewTemplate,
    employees, fetchEmployees,
  } = usePhishPass();

  const [loading, setLoading] = useState(true);
  const [showSend, setShowSend] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Send form state
  const [step, setStep] = useState('template'); // template | recipients | review
  const [chosenTemplate, setChosenTemplate] = useState(null);
  const [campaignName, setCampaignName] = useState('');
  const [recipientMode, setRecipientMode] = useState('manual'); // 'manual' | 'random'
  const [randomCount, setRandomCount] = useState(5);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchCampaigns(), fetchTemplates(), fetchEmployees()]);
      setLoading(false);
    })();
  }, []);

  const resetSendForm = () => {
    setStep('template');
    setChosenTemplate(null);
    setCampaignName('');
    setRecipientMode('manual');
    setRandomCount(5);
    setSelectedEmails([]);
    setPreviewHtml('');
    setEmpSearch('');
  };

  const openSendDialog = () => {
    resetSendForm();
    setShowSend(true);
  };

  const handlePickTemplate = async (tpl) => {
    setChosenTemplate(tpl);
    const preview = await previewTemplate(tpl.id);
    setPreviewHtml(preview?.body_html || '');
    setStep('recipients');
  };

  const toggleEmail = (email) => {
    setSelectedEmails((prev) => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const handleSend = async () => {
    if (!chosenTemplate) return;
    setSending(true);
    let payload = {
      template_id: chosenTemplate.id,
      name: campaignName || undefined,
    };
    if (recipientMode === 'random') {
      payload.random_count = Math.max(1, Number(randomCount) || 1);
      payload.recipients = [];
    } else {
      const picks = employees.filter(e => selectedEmails.includes(e.email));
      if (picks.length === 0) {
        toast.error('Pick at least one recipient');
        setSending(false);
        return;
      }
      payload.recipients = picks.map(p => ({ employee_id: p.employee_id, email: p.email, name: p.name }));
    }
    const result = await sendCampaign(payload);
    setSending(false);
    if (result.success) {
      toast.success(`Campaign sent — ${result.data.sent_count} sent, ${result.data.failed_count} failed`);
      setShowSend(false);
      resetSendForm();
      await fetchCampaigns();
    } else {
      toast.error(result.error || 'Send failed');
    }
  };

  const openDetail = async (id) => {
    const data = await getCampaign(id);
    setDetailData(data);
    setShowDetail(true);
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"? This also removes all tracking data for this send.`)) return;
    const r = await deleteCampaign(c.id);
    if (r.success) toast.success('Campaign deleted'); else toast.error(r.error);
  };

  const handleResetClicks = async () => {
    if (!detailData) return;
    if (!window.confirm(`Reset all click + open data for "${detailData.name}"?\n\nUse this if you suspect email security scanners (Microsoft Safe Links, Mimecast, etc.) falsely registered clicks before the user actually clicked.`)) return;
    const r = await resetCampaignClicks(detailData.id);
    if (r.success) {
      toast.success(`Reset click data for ${r.data.recipients_reset} recipients`);
      // Reload campaign detail + campaigns list
      const fresh = await getCampaign(detailData.id);
      setDetailData(fresh);
      await fetchCampaigns();
    } else {
      toast.error(r.error || 'Reset failed');
    }
  };

  const filteredEmployees = employees.filter(e => {
    if (!empSearch) return true;
    const q = empSearch.toLowerCase();
    return (e.name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="campaign-list">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Campaigns</h2>
          <p className="text-sm text-slate-500">Send phishing simulation emails and track who clicks.</p>
        </div>
        <Button onClick={openSendDialog} className="bg-red-600 hover:bg-red-700" data-testid="send-campaign-btn">
          <Send className="w-4 h-4 mr-1.5" />Send New Campaign
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : campaigns.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-500">
          No campaigns yet. Click <b>Send New Campaign</b> to launch your first phishing simulation.
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-xs text-slate-500 uppercase">
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">Template</th>
                  <th className="px-4 py-3 text-center">Sent</th>
                  <th className="px-4 py-3 text-center">Failed</th>
                  <th className="px-4 py-3 text-center">Opened</th>
                  <th className="px-4 py-3 text-center">Clicked</th>
                  <th className="px-4 py-3 text-center">Click %</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-slate-50" data-testid={`campaign-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => openDetail(c.id)} className="text-blue-600 hover:underline font-medium text-left" data-testid={`campaign-name-${c.id}`}>{c.name}</button>
                      <div className="text-xs text-slate-400">{c.created_at?.slice(0, 16).replace('T', ' ')}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{c.template_name}</td>
                    <td className="px-4 py-3 text-center">{c.sent_count}</td>
                    <td className="px-4 py-3 text-center">
                      {c.failed_count > 0 ? (
                        <button onClick={() => openDetail(c.id)} className="text-red-600 font-semibold hover:underline" data-testid={`failed-count-${c.id}`} title="Click to see failure reasons">
                          {c.failed_count} ⚠
                        </button>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">{c.opened_count}</td>
                    <td className="px-4 py-3 text-center">{c.clicked_count}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={c.click_rate >= 30 ? 'bg-red-100 text-red-700' : c.click_rate > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                        {c.click_rate}%
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c)} data-testid={`delete-campaign-${c.id}`}>
                        <Trash2 className="w-4 h-4 text-slate-400" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Send Campaign Dialog */}
      <Dialog open={showSend} onOpenChange={(o) => { if (!sending) { setShowSend(o); if (!o) resetSendForm(); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="send-dialog">
          <DialogHeader>
            <DialogTitle>Send Phishing Simulation</DialogTitle>
          </DialogHeader>

          <Tabs value={step} onValueChange={setStep}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="template">1. Template</TabsTrigger>
              <TabsTrigger value="recipients" disabled={!chosenTemplate}>2. Recipients</TabsTrigger>
              <TabsTrigger value="review" disabled={!chosenTemplate}>3. Review &amp; Send</TabsTrigger>
            </TabsList>

            <TabsContent value="template" className="space-y-3 mt-4">
              <div className="text-sm text-slate-600">Pick a phishing template to send:</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                {templates.map(t => (
                  <button key={t.id} onClick={() => handlePickTemplate(t)} className={`text-left border rounded-lg p-3 hover:border-blue-400 hover:bg-blue-50 transition ${chosenTemplate?.id === t.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`} data-testid={`template-pick-${t.id}`}>
                    <div className="font-medium text-sm text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t.scenario_type}</div>
                    <div className="text-xs text-slate-400 mt-2 truncate">Subject: {t.subject}</div>
                  </button>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="recipients" className="space-y-4 mt-4">
              <div>
                <Label className="text-xs">Campaign name (optional)</Label>
                <Input value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder={`${chosenTemplate?.name} — ${new Date().toISOString().slice(0,10)}`} data-testid="campaign-name-input" />
              </div>
              <div className="flex gap-2">
                <Button variant={recipientMode === 'manual' ? 'default' : 'outline'} size="sm" onClick={() => setRecipientMode('manual')} data-testid="mode-manual"><Users className="w-3.5 h-3.5 mr-1" />Pick employees</Button>
                <Button variant={recipientMode === 'random' ? 'default' : 'outline'} size="sm" onClick={() => setRecipientMode('random')} data-testid="mode-random"><Shuffle className="w-3.5 h-3.5 mr-1" />Send to random</Button>
              </div>

              {recipientMode === 'random' ? (
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <Label className="text-xs">Number of random employees</Label>
                  <Input type="number" min="1" max={employees.length} value={randomCount} onChange={e => setRandomCount(e.target.value)} data-testid="random-count-input" />
                  <div className="text-xs text-slate-500">Will pick from {employees.length} active employees in Asset Manager.</div>
                </div>
              ) : (
                <div className="border rounded-lg">
                  <div className="p-2 border-b bg-slate-50 flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-400" />
                    <Input value={empSearch} onChange={e => setEmpSearch(e.target.value)} placeholder="Search employees by name or email…" className="border-0 focus-visible:ring-0 h-8" data-testid="emp-search" />
                    <Badge variant="outline">{selectedEmails.length} selected</Badge>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {filteredEmployees.length === 0 ? (
                      <div className="p-4 text-sm text-slate-400 text-center">No employees match.</div>
                    ) : filteredEmployees.map(e => (
                      <label key={e.email} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0">
                        <Checkbox checked={selectedEmails.includes(e.email)} onCheckedChange={() => toggleEmail(e.email)} data-testid={`emp-checkbox-${e.email}`} />
                        <div className="flex-1">
                          <div className="text-sm">{e.name || e.email}</div>
                          <div className="text-xs text-slate-400">{e.email}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep('template')}>Back</Button>
                <Button onClick={() => setStep('review')} data-testid="goto-review-btn">Next: Review →</Button>
              </div>
            </TabsContent>

            <TabsContent value="review" className="space-y-4 mt-4">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
                ⚠️ You&apos;re about to send a phishing simulation email. Tracking links + a 1x1 pixel will be embedded. Make sure your &quot;Admin Recipients&quot; list is set up if you want HIGH_RISK alerts.
              </div>
              <div className="space-y-2 text-sm">
                <div><b>Template:</b> {chosenTemplate?.name}</div>
                <div><b>Subject:</b> {chosenTemplate?.subject}</div>
                <div><b>Sender:</b> {chosenTemplate?.sender_display_name} &lt;{chosenTemplate?.sender_email_override || 'default'}&gt;</div>
                <div><b>Recipients:</b> {recipientMode === 'random' ? `${randomCount} random employees` : `${selectedEmails.length} picked`}</div>
              </div>
              <div className="border rounded-lg">
                <div className="bg-slate-50 px-3 py-2 border-b text-xs font-semibold text-slate-600">Email Preview</div>
                <div className="p-4 text-sm max-h-72 overflow-y-auto" dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep('recipients')}>Back</Button>
                <Button onClick={handleSend} disabled={sending} className="bg-red-600 hover:bg-red-700" data-testid="confirm-send-btn">
                  {sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Sending…</> : <><Send className="w-4 h-4 mr-1" />Send Campaign</>}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Campaign Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="campaign-detail-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4">
              <DialogTitle className="flex-1 truncate">{detailData?.name}</DialogTitle>
              {detailData && (detailData.recipients || []).some(r => r.clicked_at || r.opened_at) && (
                <Button variant="outline" size="sm" onClick={handleResetClicks} className="text-amber-700 border-amber-300 hover:bg-amber-50 flex-shrink-0" data-testid="reset-clicks-btn">
                  <AlertCircle className="w-3.5 h-3.5 mr-1" />Reset click data
                </Button>
              )}
            </div>
          </DialogHeader>
          {detailData && (() => {
            const recipients = detailData.recipients || [];
            const sentCount = recipients.filter(r => r.send_status === 'SENT').length;
            const failedCount = recipients.filter(r => r.send_status === 'FAILED').length;
            const failedRecipients = recipients.filter(r => r.send_status === 'FAILED' && r.send_error);
            const uniqueErrors = Array.from(new Set(failedRecipients.map(r => r.send_error)));
            return (
            <div className="space-y-3">
              <div className="text-sm text-slate-600 flex flex-wrap gap-4">
                <div><b>Template:</b> {detailData.template_name}</div>
                <div><b>Total recipients:</b> {detailData.recipient_count}</div>
                <div className="text-emerald-700"><b>Sent:</b> {sentCount}</div>
                {failedCount > 0 && <div className="text-red-700"><b>Failed:</b> {failedCount}</div>}
              </div>

              {failedCount > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm" data-testid="failure-summary">
                  <div className="font-semibold text-red-800 mb-2 flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4" />
                    {failedCount} email{failedCount > 1 ? 's' : ''} failed to send
                  </div>
                  {uniqueErrors.map((err, i) => (
                    <div key={i} className="text-xs text-red-700 bg-white border border-red-100 rounded px-2 py-1.5 mb-1 font-mono">
                      {err}
                    </div>
                  ))}
                  {uniqueErrors.some(e => e && /not authorized to send/i.test(e)) && (
                    <div className="mt-2 text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded p-2">
                      💡 <b>Fix:</b> The template&apos;s <i>Sender Email Override</i> uses a domain not verified in Resend. Either:<br/>
                      &nbsp;&nbsp;• Leave the override blank (uses the default verified sender), <b>or</b><br/>
                      &nbsp;&nbsp;• Verify that domain in Resend dashboard first → then use it here.
                    </div>
                  )}
                </div>
              )}

              <table className="w-full text-sm border">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs text-slate-500 uppercase">
                    <th className="px-3 py-2">Recipient</th>
                    <th className="px-3 py-2">Send Status</th>
                    <th className="px-3 py-2 text-center">Opened</th>
                    <th className="px-3 py-2 text-center">Clicked</th>
                    <th className="px-3 py-2">Last Click</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map(r => (
                    <React.Fragment key={r.id}>
                      <tr className="border-t" data-testid={`recipient-row-${r.email}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium">{r.name || r.email}</div>
                          <div className="text-xs text-slate-400">{r.email}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={r.send_status === 'SENT' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} data-testid={`status-${r.email}`}>
                            {r.send_status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-center">{r.opened_at ? <Eye className="w-4 h-4 text-emerald-600 mx-auto" /> : '—'}</td>
                        <td className="px-3 py-2 text-center">{r.clicked_at ? <MousePointerClick className="w-4 h-4 text-red-600 mx-auto" /> : '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{r.clicked_at?.slice(0, 16).replace('T', ' ') || '—'}</td>
                      </tr>
                      {r.send_status === 'FAILED' && r.send_error && (
                        <tr className="bg-red-50/50" data-testid={`error-row-${r.email}`}>
                          <td colSpan={5} className="px-3 py-2 text-xs">
                            <div className="flex items-start gap-1.5 text-red-700">
                              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <div className="font-mono">{r.send_error}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignList;
